import { useState, useEffect, useMemo } from "react";
import { Button, Modal, Input, Spin, message } from "antd";
import { Plus, X, Image as ImageIcon } from "react-feather";
import styles from "./stylesPopularTabs.module.css";
// Import AddNewIpPopup styles for consistent modal styling
import "../../../WhiteList/AddNewIpPopup/stylesAddNewIpPopup.css";
// Import API functions
import { API_ROUTES } from "../../../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";
import SortingTable from "../SortingTable/SortingTable";
import { useSportsbookFm } from "../SportsbookFmContext";
import { RenameEntityModal } from "../Translations/RenameEntityModal";
import {
  applyListEntityRename,
  applyListEntityTranslations,
  getListEntityBaseName,
  toListTranslationEditorData,
} from "../Translations/listTranslationData";
import { useProductFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";

const PopularTabs = () => {
  const { canEdit } = useSportsbookFm();
  const { isEnabled: translationsFlagEnabled } = useProductFeatureFlag(
    "fm_translations",
    TRIVELTA_PRODUCTS.SPORTSBOOK
  );
  const translationsEnabled = translationsFlagEnabled === true;
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [addByIdMode, setAddByIdMode] = useState(false);
  const [matchId, setMatchId] = useState("");
  const [matchIdLoading, setMatchIdLoading] = useState(false);

  // Rename state
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);
  const renameTranslationData = useMemo(
    () =>
      toListTranslationEditorData(
        renameTarget,
        renameTarget?.id,
        renameTarget?.ename ?? ""
      ),
    [renameTarget]
  );
  const renameInitialName = translationsEnabled
    ? getListEntityBaseName(renameTarget, renameTarget?.ename ?? "")
    : (renameTarget?.ename ?? "");

  // Get token and create API instance
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );

  const [popularTabs, setPopularTabs] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);

  // API Functions
  const fetchPopularTabs = async () => {
    try {
      setLoading(true);
      const response = await PrivateAdminPanelInstance.get(
        API_ROUTES.GET_POPULAR_TABS
      );

      if (response.data.success) {
        const popularTabsData = response.data.data.items.map((item) => ({
          id: item?.entity_id || "",
          ename: item?.display_name || "",
          order: item?.display_order?.toString() || "",
          is_active: item?.is_active || "",
          type: item?.item_type || "",
          is_pinned: item?.is_pinned || "",
          sport: item?.sport_name || "",
          icon: item?.image_link || "",
          league_id: item?.league_id || "",
          sport_id: item?.sport_id || "",
          event_id: item?.event_id || "",
          translations: item?.translations || null,
        }));

        setPopularTabs(popularTabsData);
      }
    } catch (error) {
      console.error("Error fetching top popularTabs:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setIsAddModalVisible(true);
  };

  const handleModalClose = () => {
    setIsAddModalVisible(false);
    setSearchValue("");
    setSelectedItem(null);
    setSearchOptions([]);
    setAddByIdMode(false);
    setMatchId("");
  };

  // Real search function using API
  const fetchSearchOptions = useMemo(() => {
    let timer;
    return (query) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        if (!query || query.length < 2) {
          setSearchOptions([]);
          return;
        }
        setSearchLoading(true);
        try {
          const response = await PrivateAdminPanelInstance.get(
            API_ROUTES.AUTOCOMPLETE_POPULAR_TABS,
            {
              params: {
                search_term: query,
              },
            }
          );

          if (response.data.success) {
            const searchData = response.data.data.map((league) => ({
              entity_id: league.entity_id,
              item_name: league.item_name,
              item_type: league.item_type,
              sport_id: league.sport_id,
              sport_name: league.sport_name,
              logo: league.logo_link,
              country_name: league.country_name,
            }));
            setSearchOptions(searchData);
          }
        } catch (error) {
          console.error("Error searching popularTabs:", error);
          message.error(
            getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED)
          );
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    };
  }, []);

  useEffect(() => {
    fetchPopularTabs();
  }, []);

  useEffect(() => {
    fetchSearchOptions(searchValue);
  }, [searchValue, fetchSearchOptions]);

  const handleAddItemConfirm = async () => {
    if (!selectedItem) return;
    setAddLoading(true);
    try {
      const itemId = selectedItem.entity_id.replace(/^(SPORT|LEAGUE|EVENT)#/, "");

      const response = await PrivateAdminPanelInstance.post(
        API_ROUTES.CREATE_NEW_POPULAR_TAB,
        {
          item_type: selectedItem.item_type,
          item_id: itemId,
          display_name: selectedItem.item_name,
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
      setIsAddModalVisible(false);
      setSearchValue("");
      setSelectedItem(null);
      setSearchOptions([]);

      // Refresh the popularTabs list
      await fetchPopularTabs();
    } catch (error) {
      console.error("Error adding item:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setAddLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
  };

  const handleAddByIdConfirm = async () => {
    if (!matchId.trim()) return;
    setMatchIdLoading(true);
    try {
      const response = await PrivateAdminPanelInstance.post(
        API_ROUTES.CREATE_NEW_POPULAR_TAB,
        {
          item_type: "EVENT",
          item_id: matchId.trim(),
          display_name: null, // API will fetch the display name
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.MATCH_ADDED_SUCCESSFULLY)
      );
      setIsAddModalVisible(false);
      setMatchId("");
      setAddByIdMode(false);

      // Refresh the popularTabs list
      await fetchPopularTabs();
    } catch (error) {
      console.error("Error adding match by ID:", error);
      message.error(getApiErrorMessage(error, AppStrings.FAILED_TO_ADD_MATCH));
    } finally {
      setMatchIdLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const toggleStatus = async (record) => {
    setIsTableLoading(true);

    try {
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.SET_POPULAR_TAB_STATUS,
        {
          entity_id: record.id,
          is_active: !record.is_active,
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );

      // Update local state
      setPopularTabs((prevData) =>
        prevData.map((item) =>
          item.id === record.id
            ? { ...item, is_active: !item.is_active }
            : item
        )
      );
    } catch (error) {
      console.error("Error toggling item status:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setIsTableLoading(false);
    }
  };

  const removeItem = async (record) => {
    setIsTableLoading(true);

    const payload = {
      entity_id: record.id,
    };

    try {
      const response = await PrivateAdminPanelInstance.delete(
        API_ROUTES.DELETE_POPULAR_TAB,
        { data: payload }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );

      // Update local state
      setPopularTabs((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
    } catch (error) {
      console.error("Error deleting item:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setIsTableLoading(false);
    }
  };

  const changeOrder = async (reorderedItems) => {
    setIsTableLoading(true);
    try {
      // Update local state first for immediate UI feedback
      setPopularTabs(reorderedItems);

      // Send API request to reorder
      const orderedIds = reorderedItems.map((item) => item.id);
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.ORDER_POPULAR_TABS,
        {
          ordered_ids: orderedIds,
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
    } catch (error) {
      console.error("Error reordering popular tabs:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
      // Revert to original state on error
      fetchPopularTabs();
    } finally {
      setIsTableLoading(false);
    }
  };

  // Rename handlers
  const handleRenameClick = (item) => {
    setRenameTarget(item);
    setIsRenameModalVisible(true);
  };

  const handleRenameModalClose = () => {
    setIsRenameModalVisible(false);
    setRenameTarget(null);
  };

  const handleRenameConfirm = async (name, translationChanges) => {
    if (!renameTarget) return;
    setRenameLoading(true);
    try {
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.RENAME_POPULAR_TAB,
        {
          entity_id: renameTarget.id,
          display_name: name,
          ...(translationChanges
            ? {
                translation_key: translationChanges.translation_key,
                translations: translationChanges.translations,
              }
            : {}),
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.ITEM_RENAMED_SUCCESSFULLY)
      );

      // Update local state
      setPopularTabs((prevData) =>
        prevData.map((item) => {
          if (item.id !== renameTarget.id) return item;
          const renamed = applyListEntityRename(
            item,
            name,
            translationsEnabled,
            "ename"
          );
          if (!translationChanges) return renamed;
          return applyListEntityTranslations(
            renamed,
            translationChanges.translations,
            "ename",
            translationChanges.translation_key
          );
        })
      );

      handleRenameModalClose();
    } catch (error) {
      console.error("Error renaming item:", error);
      message.error(getApiErrorMessage(error, AppStrings.FAILED_TO_RENAME_ITEM));
    } finally {
      setRenameLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>{AppStrings.POPULAR_TABS}</h2>
        {canEdit && (
          <Button
            type="primary"
            icon={<Plus size={16} />}
            onClick={handleAddItem}
            className={styles.addButton}
          >
            {AppStrings.ADD_ITEM || "Add Item"}
          </Button>
        )}
      </div>

      <div className={styles.tableContainer}>
        <SortingTable
          columns={[
            "",
            "#Order",
            "Name",
            "Type",
            "Status",
            "Action",
          ]}
          data={popularTabs}
          setData={setPopularTabs}
          toggleStatus={toggleStatus}
          removeItem={removeItem}
          onRename={handleRenameClick}
          changeOrder={changeOrder}
          isLoading={isTableLoading}
          canEdit={canEdit}
        />
      </div>

      <Modal
        closable={false}
        open={isAddModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        className="add-league-modal"
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">{AppStrings.ADD_POPULAR_TAB}</h2>
            </div>
            <X onClick={handleModalClose} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            {/* Toggle between Search and Add by ID */}
            <div className={styles.modeToggle}>
              <button
                className={`${styles.modeButton} ${!addByIdMode ? styles.modeButtonActive : ""}`}
                onClick={() => setAddByIdMode(false)}
              >
                {AppStrings.SEARCH}
              </button>
              <button
                className={`${styles.modeButton} ${addByIdMode ? styles.modeButtonActive : ""}`}
                onClick={() => setAddByIdMode(true)}
              >
                {AppStrings.ADD_MATCH_BY_ID}
              </button>
            </div>

            {!addByIdMode ? (
              /* Search Mode */
              <div className="autocomplete">
                <Input
                  placeholder={AppStrings.SEARCH_SPORTS_LEAGUES_MATCHES}
                  value={searchValue}
                  onChange={handleSearchChange}
                />
                {searchValue ? (
                  <div>
                    {searchLoading ? (
                      <div className="center">
                        <Spin size="small" />
                      </div>
                    ) : (
                      searchOptions?.map((option) => (
                        <div
                          key={option.entity_id}
                          className={`${styles.leagueCard} ${selectedItem?.entity_id === option.entity_id
                            ? styles.selected
                            : ""
                            }`}
                          onClick={() => handleItemSelect(option)}
                        >
                          <div className={styles.leagueLogo}>
                            {option?.logo ? (
                              <img
                                src={option?.logo}
                                alt="Logo"
                                className={styles.logoEmoji}
                              />
                            ) : (
                              <ImageIcon size={18} />
                            )}
                          </div>
                          <div className={styles.leagueInfo}>
                            <span className={styles.leagueName}>
                              {option.item_name}
                            </span>
                          </div>
                          {option.country_name && (
                            <div className={styles.countryTag}>
                              <span className={styles.countryName}>
                                {option.country_name}
                              </span>
                            </div>
                          )}
                          <div className={styles.typeTag}>
                            <span className={styles.typeText}>
                              {option.item_type}
                            </span>
                          </div>
                          {option.sport_name && (
                            <div className={styles.sportTag}>
                              <span className={styles.sportText}>
                                {option.sport_name}
                              </span>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                    {!searchLoading &&
                      searchOptions.length === 0 &&
                      searchValue && (
                        <div className="empty-mini">
                          {AppStrings.NO_RESULTS_FOUND}
                        </div>
                      )}
                  </div>
                ) : null}
              </div>
            ) : (
              /* Add by ID Mode */
              <div className={styles.addByIdContainer}>
                <label className={styles.addByIdLabel}>{AppStrings.MATCH_ID}</label>
                <Input
                  placeholder={AppStrings.ENTER_MATCH_ID}
                  value={matchId}
                  onChange={(e) => setMatchId(e.target.value)}
                  onPressEnter={handleAddByIdConfirm}
                />
                <p className={styles.addByIdHint}>
                  {AppStrings.ADD_MATCH_BY_ID_HINT}
                </p>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={handleModalClose}
              disabled={addLoading || matchIdLoading}
            >
              {AppStrings.CLOSE}
            </button>
            {!addByIdMode ? (
              <button
                className={`submit-button ${!selectedItem || addLoading ? "disabled" : ""}`}
                disabled={!selectedItem || addLoading}
                onClick={handleAddItemConfirm}
              >
                {addLoading ? AppStrings.ADDING : AppStrings.ADD_ITEM}
              </button>
            ) : (
              <button
                className={`submit-button ${!matchId.trim() || matchIdLoading ? "disabled" : ""}`}
                disabled={!matchId.trim() || matchIdLoading}
                onClick={handleAddByIdConfirm}
              >
                {matchIdLoading ? AppStrings.ADDING : AppStrings.ADD_MATCH}
              </button>
            )}
          </div>
        </div>
      </Modal>

      <RenameEntityModal
        open={isRenameModalVisible}
        title={AppStrings.RENAME_POPULAR_TAB}
        nameLabel={AppStrings.DISPLAY_NAME}
        initialName={renameInitialName}
        entityKey={renameTarget?.translations?.key ?? renameTarget?.id}
        initialTranslationData={renameTranslationData}
        translationsEnabled={translationsEnabled}
        bundleTranslationChanges={translationsEnabled}
        saving={renameLoading}
        onSave={handleRenameConfirm}
        onClose={handleRenameModalClose}
      />
    </div>
  );
};

export default PopularTabs;
